import { useBreakpointValue } from "@chakra-ui/react"
import scrollNearestParent from "@pathwright/utils/src/scroll/scrollNearestParent"
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { createContext, useContextSelector } from "use-context-selector"
import {
  CoverPageResourceDocument,
  PathItemType,
  ResourceType,
  useCoverPageOffersQuery,
  useCoverPageRelatedResourcesQuery
} from "../api/generated"
import { createPathFromItems } from "../path/utils"
import { flattenEdges, useQuery } from "../utils/apollo"
import {
  CoverPageCategory,
  CoverPageContextType,
  CoverPagePreviewPathItem,
  CoverPageProps,
  CoverPageResource
} from "./types"

const CoverPageContext = createContext<CoverPageContextType>(
  {} as CoverPageContextType
)

const useRelatedResources = (resource?: CoverPageResource) => {
  const categories = useMemo(
    () => flattenEdges(resource?.categories),
    [resource?.categories]
  )

  const categoryIds =
    categories?.map((category: CoverPageCategory) => category.id) || []

  const relatedResourcesQuery = useCoverPageRelatedResourcesQuery({
    variables: {
      idFilter: { neq: resource?.id?.toString() },
      categoryIdFilter: {
        in: categoryIds.map((id: number) => id?.toString() || "")
      },
      resourceTypeFilter: { eq: ResourceType.Course },
      orderBy: "list_order,-created",
      first: 10
    },
    skip: !categoryIds.length
  })

  const resources = flattenEdges(relatedResourcesQuery?.data?.resources || [])

  return resources
}

export function useCoverPageContext<T = CoverPageContextType>(
  selector?: (value: CoverPageContextType) => T
): T {
  if (selector) {
    return useContextSelector(CoverPageContext, selector)
  }
  return useContextSelector(CoverPageContext, (v) => v) as T
}

type CoverPageProviderProps = CoverPageProps & {
  children: ReactNode
}

const CoverPageProvider = ({ children, ...props }: CoverPageProviderProps) => {
  const [canPreview, setCanPreview] = useState(false)
  const [videoFocused, setVideoFocused] = useState(false)
  const [backgroundVideoMuted, setBackgroundVideoMuted] = useState(true)
  const [showVideoMuteButton, setShowVideoMuteButton] = useState<boolean>(false)
  const [loadingNextCoverPage, setLoadingNextCoverPage] = useState(false)

  // Used mainly for scrolling to sections
  const offersSectionRef = useRef<HTMLDivElement>(null)
  const previewSectionRef = useRef<HTMLDivElement>(null)
  const contributorSectionRef = useRef<HTMLDivElement>(null)
  const relatedSectionRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Header collapsed/trigger shared state
  const headerTriggerRef = useRef<HTMLDivElement>(null)
  const [headerCollapsed, setHeaderCollapsed] = useState(false)

  const isMobile = useBreakpointValue({
    base: true,
    md: false
  })

  const scrollToHeaderTrigger = useCallback(() => {
    if (headerTriggerRef.current) {
      scrollNearestParent(headerTriggerRef.current, {
        top: 0,
        behavior: "smooth"
      })
    }
  }, [])

  const { data: resourceData } = useQuery(CoverPageResourceDocument, {
    variables: { id: props.resourceId },
    skip: !props.resourceId
  })
  const resource = resourceData?.resource

  const { data: offerData, loading: offersLoading } = useCoverPageOffersQuery({
    variables: { resourceId: resourceData?.resource?.id, first: 100 },
    skip: !resourceData?.resource?.id
  })

  // Memoize flattened data to prevent unnecessary rerenders
  const contributors = useMemo(
    () => flattenEdges(resource?.contributors),
    [resource]
  )
  const categories = useMemo(
    () => flattenEdges(resource?.categories),
    [resource]
  )
  const offers = useMemo(
    () => flattenEdges(offerData?.offerConnections),
    [offerData?.offerConnections]
  )

  const relatedResources = useRelatedResources(resource)

  // Memoize preview navigation items
  // We need this in the provider so it can be used in the immersive routes
  // to determine which navigation items are available
  const previewNavItems = useMemo(() => {
    if (!resource?.group?.path) return []

    const flattenedPathItems = flattenEdges(
      resource.group.path.items || []
    ) as CoverPagePreviewPathItem[]
    const pathItems = createPathFromItems(flattenedPathItems)

    // Build navigation items: "path" + previewable step source_ids
    const previewableSteps = pathItems.reduce((acc, item) => {
      if (item.items) {
        acc.push(
          ...item.items.filter(
            (i) => i.is_previewable && i.type === PathItemType.Step
          )
        )
      }
      return acc
    }, [] as CoverPagePreviewPathItem[])

    return [
      { source_id: "path" as const },
      ...previewableSteps.map((s) => ({ source_id: s.source_id as number }))
    ]
  }, [resource?.group?.path])

  // Stabilize external handlers via refs so context value doesn't change
  const onCloseRef = useRef(props.onClose)
  const onOpenImmersiveRef = useRef(props.onOpenImmersive)

  const immersiveActive = useMemo(() => {
    return props.immersiveRoute !== null
  }, [props.immersiveRoute])

  useEffect(() => {
    onCloseRef.current = props.onClose
  }, [props.onClose])
  useEffect(() => {
    onOpenImmersiveRef.current = props.onOpenImmersive
  }, [props.onOpenImmersive])

  // Reset loading state when resource changes (new Cover Page loaded)
  useEffect(() => {
    if (resource) {
      setLoadingNextCoverPage(false)
    }
  }, [resource])

  const stableOnClose = useCallback(() => {
    onCloseRef.current?.()
  }, [])
  const stableOnOpenImmersive = useCallback((route: any) => {
    onOpenImmersiveRef.current?.(route)
  }, [])

  const contextValue = useMemo(() => {
    if (!resource) return null

    return {
      // Props
      onClose: stableOnClose,
      onOpenImmersive: stableOnOpenImmersive,
      immersiveRoute: props.immersiveRoute,

      // From GQL
      resource,
      contributors,
      categories,
      offers,
      offersLoading,
      relatedResources,

      // Refs
      offersSectionRef,
      previewSectionRef,
      contributorSectionRef,
      relatedSectionRef,
      scrollContainerRef,
      headerTriggerRef,

      // State
      canPreview,
      setCanPreview,
      videoFocused,
      setVideoFocused,
      backgroundVideoMuted,
      setBackgroundVideoMuted,
      headerCollapsed,
      setHeaderCollapsed,
      showVideoMuteButton,
      setShowVideoMuteButton,
      loadingNextCoverPage,
      setLoadingNextCoverPage,

      // Computed values
      immersiveActive,
      isMobile,

      // Handlers
      scrollToHeaderTrigger,

      // Preview navigation data
      previewNavItems
    } satisfies CoverPageContextType
  }, [
    contributors,
    categories,
    offers,
    resource,
    offersLoading,
    canPreview,
    videoFocused,
    backgroundVideoMuted,
    headerCollapsed,
    showVideoMuteButton,
    immersiveActive,
    isMobile,
    loadingNextCoverPage,
    previewNavItems,
    props.immersiveRoute
  ])

  if (!contextValue) return null

  return (
    <CoverPageContext.Provider value={contextValue}>
      {children}
    </CoverPageContext.Provider>
  )
}

export default CoverPageProvider
